import Button from "~/components/ui/Button";
import Icon from "~/components/ui/Icon";
import Image from "~/components/ui/Image";
import { useSuggestions } from "~/sdk/useSuggestions";
import { useStore } from "@tanstack/react-store";
import React, { useEffect, useRef, useState } from "react";
import { useId } from "react";
import { sendEvent } from "~/sdk/analytics";
import { useUI } from "~/sdk/useUI";
import { getProductImageAlt } from "~/utils/product";
import { sanitizeStoreUrl } from "~/utils/constants";
import { type Resolved } from "~/types/deco";
import { Suggestion } from "@decocms/apps-commerce/types";

export interface Props {
  placeholder?: string;
  action?: string;
  name?: string;
  loader: Resolved<Suggestion | null>;
}

function FloatingSearchButton({
  placeholder = "Busca",
  action = "/s",
  name = "q",
  loader,
}: Props) {
  const id = useId();
  const searchType = "floating";
  const { displayFloatingSearch } = useUI();
  const isFloatingSearchOpen = useStore(displayFloatingSearch.store, (s) => s);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { setQuery, payload, loading } = useSuggestions(loader);
  const payloadValue = useStore(payload.store, (s) => s);
  const loadingValue = useStore(loading.store, (s) => s);

  useEffect(() => {
    if (isFloatingSearchOpen) {
      setQuery("");
    } else if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  }, [isFloatingSearchOpen]);
  const { products, searches = [] } = payloadValue ?? {};
  const suggestedProducts = products ?? [];
  // Referência ou nome sem histórico de busca (ex: "8348E") só tem produtos.
  const hasTerms = Boolean(searches.length || suggestedProducts.length);
  const suggestionClass = `suggestions-${searchType}`;
  const suggestionId = `search-suggestion-${searchType}-${id}`;

  const [isInputFocused, setIsInputFocused] = useState(false);

  const handleBlur = (e: React.FocusEvent<HTMLFormElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;

    if (
      relatedTarget && relatedTarget.closest(`.${suggestionClass}`) !== null
    ) {
      return;
    }
    return;
  };

  return (
    <>
      <Button
        className="btn-circle btn-sm btn-ghost flex justify-center items-center"
        aria-label="Exibir busca"
        onClick={() => (displayFloatingSearch.value = !displayFloatingSearch
          .value)}
      >
        <Icon id="MagnifyingGlass2" size={20} strokeWidth={0.1} />
      </Button>

      <div
        className={`fixed h-[38px] top-[135px] left-[50%] -translate-x-[50%] w-[80vw] rounded-[25px] border-[1px] border-[#d7d7d7] bg-white ${
          isFloatingSearchOpen ? "block" : "hidden"
        }`}
      >
        <form
          id={id}
          action={action}
          onFocus={() => setIsInputFocused(true)}
          onBlur={handleBlur}
          className="flex h-full"
        >
          <input
            ref={searchInputRef}
            id={`search-input-${searchType}-${id}`}
            className="bg-transparent border-none rounded-none font-['Poppins'] text-base w-[90%] px-[30px] md:w-[93%]"
            name={name}
            onChange={(e) => {
              const value = e.currentTarget.value;
              if (value) {
                sendEvent({
                  name: "search",
                  params: { search_term: value },
                });
              }
              setQuery(value);
            }}
            placeholder={placeholder}
            role="combobox"
            aria-labelledby={`search-label-${searchType}-${id}`}
            aria-describedby={`search-description-${searchType}-${id}`}
            aria-controls={suggestionId}
            aria-autocomplete="list"
          />
          <span id={`search-label-${searchType}-${id}`} className="sr-only">
            Search
          </span>
          <span id={`search-description-${searchType}-${id}`} className="sr-only">
            Enter your search term
          </span>
          <Button
            type="submit"
            className="h-10 w-[10%] md:w-[7%]"
            aria-label="Busca"
            tabIndex={-1}
          >
            {loadingValue
              ? <span className="loading loading-spinner loading-xs" />
              : <Icon id="MagnifyingGlass" size={24} strokeWidth={0.01} />}
          </Button>
        </form>
      </div>

      <div
        className={`${suggestionClass} fixed bg-white top-[187px] left-[50%] -translate-x-[50%] w-[80%] rounded-2.5 ${
          isInputFocused && hasTerms ? "" : "hidden"
        }`}
      >
        <ul
          id={suggestionId}
          className="flex flex-col w-full p-[2.5px] font-['Poppins'] text-sm"
        >
          {searches.map(({ term }) => (
            <li className="group p-2.5 min-h-10 hover:bg-[#C18A58]">
              <a
                className="flex flex-row items-center w-full text-black group-hover:text-white"
                href={`/s?q=${term}`}
              >
                <span>{term}</span>
              </a>
            </li>
          ))}
          {suggestedProducts.map((product) => (
            <li className="group p-2.5 w-full min-h-10 hover:bg-[#C18A58]">
              <a
                className="flex flex-row items-center gap-[5px] w-full text-black group-hover:text-white"
                href={sanitizeStoreUrl(product.url)}
              >
                {product?.image && product?.image?.length && (
                  <Image
                    width={25}
                    height={25}
                    src={product.image[0].url ?? ""}
                    alt={getProductImageAlt(product.isVariantOf?.name, 0)}
                    loading="lazy"
                    fetchPriority="low"
                    decoding="async"
                    className="shrink-0"
                  />
                )}
                <span>
                  {product?.name && product?.isVariantOf?.name
                    ? product?.isVariantOf?.name?.replace(product?.name, "")
                    : ""}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

export default FloatingSearchButton;
